import React, { createContext, ReactNode, useCallback } from "react";
import i18n, { TOptions } from "i18next";
import Backend from "i18next-http-backend";
import { initReactI18next, useTranslation } from "react-i18next";
import { setJwtToken } from "../common/api/apiShared";
import { isNullOrWhitespace } from "../common/stringOperations";
import { getSettings } from "../common/settingsProvider";
import { PlanType } from "../auth/planProvider/PlanProvider.types";
import { OperationCriticalityLevelEnum } from "../system/systemEdit/SystemEdit.types.ts";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

export type TranslationStringType = (key: string, options?: TOptions | string) => string;

// The times reach here as a UTC time of day that the browser parsed as local, since the backend sends them without a
// zone. Moving the instant rather than adding the offset to the hour keeps the result a time of day: 22:09 in UTC+2
// is 00:09, not 24:09.
function convertUtcTimeOfDayToLocal(date: Date, timezoneOffset?: number) {
    const offsetInMinutes = timezoneOffset ?? date.getTimezoneOffset();
    return new Date(date.getTime() - offsetInMinutes * 60000);
}

function convertLocalHoursToUTC(date: Date) {
    var offset = date.getTimezoneOffset() / 60;
    var hours = date.getHours();
    return hours + offset;
}

export function convertHoursOrMinutesToCorrectFormat(number: number) {
    return number < 10 ? "0" + number : number;
}

// Deliberately UTC, unlike the local-time month formatters: the callers label UTC month boundaries (e.g. the
// risk distribution history's end-of-month instants, 23:59:59Z), which local formatting would shift into the
// neighbouring month for any timezone away from UTC. Cached per locale because Intl.DateTimeFormat
// construction is the expensive half of Intl and chart callers format one label per data point per render.
const utcShortMonthAndYearFormatters = new Map<string, Intl.DateTimeFormat>();

function getUtcShortMonthAndYearFormatter(locale: string) {
    let formatter = utcShortMonthAndYearFormatters.get(locale);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", timeZone: "UTC" });
        utcShortMonthAndYearFormatters.set(locale, formatter);
    }
    return formatter;
}

export const setupi18n = (defaultLanguage: string, cacheKey: string) => {
    const settings = getSettings();

    i18n.use(Backend)
        .use(initReactI18next)
        .init({
            backend: {
                loadPath: `${settings.apiBaseUrl}/translations/{{lng}}?cache-bust=${cacheKey}`,
            },
            debug: false,
            lng: defaultLanguage,
            returnEmptyString: false,
            fallbackLng: "en",
            interpolation: { escapeValue: false },
        });
};

export const TranslationContext = createContext<{
    translateString: TranslationStringType;
    translateNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
    translateDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
    translateDateWithTime: (date: Date) => string;
    translateCountry: (countryCode: string) => string;
    translateCountriesObject: () => {};
    translateLanguage: (countryCode: string) => string;
    changeLanguage: (languageCode: string) => void;
    getBrowserLanguage: () => string;
    getChosenLanguage: () => string;
    getChosenDateFormat: () => string;
    translateDocumentType: (type: string) => string;
    translateCustomListType: (type: string) => string;
    translateAnnualWheelCategoryType: (type: string) => string;
    translateNIS2Category: (type: string) => string;
    translateISO27001CategoryTypes: (type: string) => string;
    translatePermissionTypes: (permission: string) => string;
    translatepermissionTypeToolTips: (permission: string) => string;
    translatePermissionLevel: (level: string) => string;
    translatePermissionListTypes: (listType: string) => string;
    translatePolicyTypes: (type: string) => string;
    translatePolicyStatuses: (status: string) => string;
    translateStatus: (status: string) => string;
    translateHelpTextTypes: (type: string) => string;
    translateauditLogTypes: (type: string) => string;
    translatePropertyName: (name: string) => string;
    translateEntityType: (name: string) => string;
    translateDateWithFullMonthAndYear: (date: Date | undefined) => string;
    translateUtcDateToShortMonthAndYear: (date: Date | undefined) => string;
    translateRiskAssessmentVersionAreas: (type: string) => string;
    translateTime: (date: Date, timezoneOffset?: number) => string;
    convertLocalHoursToUTC: (date: Date) => number;
    translateShortMonth: (month: number) => string;
    getAllPropertyNames: () => Array<{ key: string; value: string }>;
    getDateTimePickerLanguage: () => "da" | "en";
    translateLegalEntityAuditStatus: (status: string) => string;
    translateLegalEntityAssetRole: (role: string) => string;
    translateDateToFullMonth: (date: Date | undefined) => string;
    translateDataSubjectsEnum: (dataSubject: string) => string;
    translateProcessorsEnum: (dataSubject: string) => string;
    translatePlanType: (planType: PlanType) => string;
    translateOperationCriticalityLevelEnum: (level: number) => string;
    translateCurrencyNames: (currency: string) => string;
    translateContractStatusEnum: (status: string) => string;
    translateRiskScenarioCategory: (category: number) => string;
    translateRiskObservationSource: (source: number) => string;
    translateRiskObservationStatus: (status: number) => string;
    translateRiskConsequenceCategory: (category: number) => string;
    translateRiskAreaAssessmentLevel: (level: number) => string;
    translateFrameworkStatusEnum: (status: string) => string;
    translateFrameworkControlStatusEnum: (status: string) => string;
    translatePercentage: (value: number) => string;
    translateDocumentTypeUsage: (usage: string) => string;
    translateAnnualWheelActivityGeneralTypeEnum: (type: string) => string;
    translateEvaluationEnum: (name: string) => string;
    translateUserResponsibilityPropertyType: (type: number) => string;
    translateUserResponsibilityEntityType: (type: number) => string;
}>({
    translateString: () => "",
    translateNumber: () => "",
    translateDate: () => "",
    translateDateWithTime: () => "",
    translateLanguage: () => "",
    translateCountry: () => "",
    translateCountriesObject: () => Object,
    changeLanguage: () => {},
    getBrowserLanguage: () => "",
    getChosenLanguage: () => "",
    getChosenDateFormat: () => "",
    translateDocumentType: () => "",
    translateCustomListType: () => "",
    translateAnnualWheelCategoryType: () => "",
    translatePolicyTypes: () => "",
    translatePolicyStatuses: () => "",
    translatePermissionTypes: () => "",
    translatepermissionTypeToolTips: () => "",
    translatePermissionLevel: () => "",
    translatePermissionListTypes: () => "",
    translateHelpTextTypes: () => "",
    translateStatus: () => "",
    translateauditLogTypes: () => "",
    translatePropertyName: () => "",
    translateEntityType: () => "",
    translateNIS2Category: () => "",
    translateISO27001CategoryTypes: () => "",
    translateDateWithFullMonthAndYear: () => "",
    translateUtcDateToShortMonthAndYear: () => "",
    translateRiskAssessmentVersionAreas: () => "",
    translateTime: () => "",
    convertLocalHoursToUTC: () => 0,
    translateShortMonth: () => "",
    getAllPropertyNames: () => [],
    getDateTimePickerLanguage: () => "en",
    translateLegalEntityAuditStatus: () => "",
    translateLegalEntityAssetRole: () => "",
    translateDateToFullMonth: () => "",
    translateDataSubjectsEnum: () => "",
    translateProcessorsEnum: () => "",
    translatePlanType: () => "",
    translateOperationCriticalityLevelEnum: () => "",
    translateCurrencyNames: () => "",
    translateContractStatusEnum: () => "",
    translateRiskScenarioCategory: () => "",
    translateRiskObservationSource: () => "",
    translateRiskObservationStatus: () => "",
    translateRiskConsequenceCategory: () => "",
    translateRiskAreaAssessmentLevel: () => "",
    translateFrameworkStatusEnum: () => "",
    translateFrameworkControlStatusEnum: () => "",
    translatePercentage: () => "",
    translateDocumentTypeUsage: () => "",
    translateAnnualWheelActivityGeneralTypeEnum: () => "",
    translateEvaluationEnum: () => "",
    translateUserResponsibilityPropertyType: () => "",
    translateUserResponsibilityEntityType: () => "",
});

export const TranslationProvider: React.FunctionComponent<{ onLanguageChanged?: (languageCode: string) => void; children: ReactNode }> = ({
    children,
    onLanguageChanged,
}) => {
    const { t } = useTranslation("");
    const queryClient = useDotLegalQueryClient();

    const getBrowserLanguage = () => navigator.language ?? "en";
    const getChosenLanguage = () => i18n.language;
    const getChoseDateFormat = () => i18n.language;
    const translateNumber = useCallback((num: number, options?: Intl.NumberFormatOptions) => num.toLocaleString(i18n.language, options), []);

    const getDateTimePickerLanguage = () => (i18n.language === "da" || i18n.language === "da-DK" ? "da" : "en");

    const translateDate = useCallback((date: Date) => {
        if (!date) {
            return "";
        }

        const localDate = new Date(date);
        let ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(localDate);
        let mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(localDate);
        let da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(localDate);
        if (getChoseDateFormat() === "da") {
            return `${da}-${mo}-${ye}`;
        }
        return `${ye}-${mo}-${da}`;
    }, []);

    const translateDateWithTime = useCallback((date: Date) => {
        if (!date) {
            return "";
        }

        const localDate = new Date(date);
        let ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(localDate);
        let mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(localDate);
        let da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(localDate);
        // The date stays where it was and only the time of day is moved: an incident stores a local calendar date
        // beside a UTC time of day, so moving the date too would put an incident just after midnight on the next day.
        const localTime = convertUtcTimeOfDayToLocal(localDate);
        let h = convertHoursOrMinutesToCorrectFormat(localTime.getHours());
        let m = convertHoursOrMinutesToCorrectFormat(localTime.getMinutes());

        return `${da}-${mo}-${ye} ${h}:${m}`;
    }, []);

    const translateDateWithFullMonthAndYear = useCallback((date: Date | undefined) => {
        if (!date) {
            return "";
        }

        const localDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(getChoseDateFormat(), { year: "numeric", month: "long" });
        const formattedDate = formatter.format(localDate);
        const capitalizedMonth = formattedDate.replace(/^\w/, (c) => c.toUpperCase());

        return capitalizedMonth;
    }, []);

    const translateUtcDateToShortMonthAndYear = useCallback((date: Date | undefined) => {
        if (!date) {
            return "";
        }

        return getUtcShortMonthAndYearFormatter(getChoseDateFormat())
            .format(new Date(date))
            .replace(/^\w/, (c) => c.toUpperCase());
    }, []);

    const translateDateToFullMonth = useCallback((date: Date | undefined) => {
        if (!date) {
            return "";
        }

        const localDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(getChoseDateFormat(), { month: "long" });
        const formattedDate = formatter.format(localDate);
        const capitalizedMonth = formattedDate.replace(/^\w/, (c) => c.toUpperCase());

        return capitalizedMonth;
    }, []);

    const translateTime = useCallback((date: Date, timezoneOffset?: number) => {
        if (!date) {
            return "";
        }

        const localTime = convertUtcTimeOfDayToLocal(new Date(date), timezoneOffset);
        let h = convertHoursOrMinutesToCorrectFormat(localTime.getHours());
        let m = convertHoursOrMinutesToCorrectFormat(localTime.getMinutes());

        return `${h}:${m}`;
    }, []);

    const changeLanguage = (language: string) => {
        const isPdf = new URLSearchParams(window.location.search).get("isPdf");
        let current = getChosenLanguage();

        if (current !== language && !isPdf) {
            localStorage.setItem("language", language);
            i18n.changeLanguage(language);

            setJwtToken("");
            queryClient.clear();
        }
    };

    const translateLanguage = (countryCode: string): string => {
        const json: any = t("languages", { returnObjects: true });
        return json[countryCode.toUpperCase()];
    };

    const translateCustomListType = (type: string) => {
        const json: any = t("customListTypes", { returnObjects: true });
        return json[type];
    };

    const translateDocumentType = (documentType: string) => {
        const json: any = t("standardDocumentTypes", { returnObjects: true });

        return json[documentType];
    };

    const translateAnnualWheelCategoryType = (categoryType: string) => {
        const json: any = t("standardAnnualWheelActivityTypes", { returnObjects: true });
        return json[categoryType];
    };

    const translateISO27001CategoryTypes = (categoryType: string) => {
        const json: any = t("iso27001CategoryTypes", { returnObjects: true });
        return json[categoryType];
    };

    const translateNIS2Category = (categoryType: string) => {
        const json: any = t("standardNIS2ActivityTemplateCategories", { returnObjects: true });
        return json[categoryType];
    };

    const translatePermissionTypes = (permission: string) => {
        const json: any = t("permissionTypes", { returnObjects: true });
        return json[permission];
    };

    const translatePolicyTypes = (type: string) => {
        const json: any = t("policyTypes", { returnObjects: true });
        return json[type];
    };

    const translatePolicyStatuses = (status: string) => {
        const json: any = t("policyStatuses", { returnObjects: true });
        return json[status];
    };

    const translateHelpTextTypes = (type: string) => {
        const json: any = t("helpTextTypes", { returnObjects: true });
        return json[type];
    };

    const translateRiskAssessmentVersionAreas = (type: string) => {
        const json: any = t("riskAssessmentVersionArea", { returnObjects: true });
        return json[type];
    };

    const translatepermissionTypeToolTips = (permission: string) => {
        const json: any = t("permissionTypeToolTip", { returnObjects: true });
        return json[permission];
    };

    const countries: any = t("countries", { returnObjects: true });
    const translateCountry = (countryCode: string): string => {
        return countries[countryCode];
    };
    const translateAllCountries = (): {} => {
        return countries;
    };

    const translateCurrencyNames = (currency: string) => {
        const json: any = t("currencyNames", { returnObjects: true });
        var key = json[currency];
        if (isNullOrWhitespace(key)) return currency;
        return key;
    };

    const translatePermissionLevel = (level: string) => {
        const json: any = t("permissionLevels", { returnObjects: true });
        return json[level];
    };

    const translatePermissionListTypes = (level: string) => {
        const json: any = t("permissionListTypes", { returnObjects: true });
        return json[level];
    };

    const translateStatus = (status: string) => {
        const json: any = t("processingActivityStatus", { returnObjects: true });
        return json[status];
    };

    const translateauditLogTypes = (type: string) => {
        const json: any = t("auditLogTypes", { returnObjects: true });
        return json[type];
    };

    const translatePropertyName = (name: string) => {
        const json: any = t("propertyNames", { returnObjects: true });
        var key = json[name];
        if (isNullOrWhitespace(key)) return name;
        return key;
    };

    const translateEntityType = (name: string) => {
        const json: any = t("entityTypes", { returnObjects: true });
        var key = json[name];
        if (isNullOrWhitespace(key)) return name;
        return key;
    };

    const translateShortMonth = (month: number) => {
        const json: any = t("shortMonth", { returnObjects: true });
        var key = json[month];
        if (isNullOrWhitespace(key)) {
            return "Invalid month";
        }
        return key;
    };

    const getAllPropertyNames = () => {
        const namesObject: any = t("propertyNames", { returnObjects: true });
        const result: Array<{ key: string; value: string }> = [];
        for (const key in namesObject) {
            result.push({ key: key, value: namesObject[key] });
        }
        return result;
    };

    const translateLegalEntityAuditStatus = (status: string) => {
        const json: any = t("legalEntityAuditStatuses", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateLegalEntityAssetRole = (status: string) => {
        const json: any = t("legalEntityAssetRoles", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateDataSubjectsEnum = (status: string) => {
        const json: any = t("processingActivityDataSubjectsEnum", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateProcessorsEnum = (status: string) => {
        const json: any = t("processingActivityProcessorsEnum", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translatePlanType = (planType: PlanType) => {
        switch (planType) {
            case PlanType.DataProtection:
                return "Data Protection";
            case PlanType.InformationSecurity:
                return "Information & Cyber Security";
            case PlanType.VendorManagement:
                return "Vendor Management";
            case PlanType.SingleSignOn:
                return "Single Sign-On";
            case PlanType.UserManagementPremium:
                return "Advanced User Management";
            case PlanType.CustomLists:
                return "Custom lists";
            case PlanType.Declarations:
                return "Declarations";
            case PlanType.OrganizationalManagement:
                return "Group Companies";
            case PlanType.ContractManagement:
                return "Contract Management";
            case PlanType.Cvr:
                return "CVR";
            default:
                return "";
        }
    };

    const translateOperationCriticalityLevelEnum = (level: OperationCriticalityLevelEnum) => {
        switch (level) {
            case OperationCriticalityLevelEnum.VeryLow:
                return t("veryLow");
            case OperationCriticalityLevelEnum.Low:
                return t("low");
            case OperationCriticalityLevelEnum.Moderate:
                return t("moderate");
            case OperationCriticalityLevelEnum.High:
                return t("high");
            case OperationCriticalityLevelEnum.VeryHigh:
                return t("veryHigh");
            default:
                return "";
        }
    };

    const translateContractStatusEnum = (status: string) => {
        const json: any = t("contractStatus", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateRiskScenarioCategory = (category: number) => {
        const json: any = t("riskScenarioCategory", { returnObjects: true });
        const key = json[category];
        if (isNullOrWhitespace(key)) return String(category);
        return key;
    };

    const translateRiskObservationSource = (source: number) => {
        const json: any = t("riskObservationSource", { returnObjects: true });
        const key = json[source];
        if (isNullOrWhitespace(key)) return String(source);
        return key;
    };

    const translateRiskObservationStatus = (status: number) => {
        const json: any = t("riskObservationStatus", { returnObjects: true });
        const key = json[status];
        if (isNullOrWhitespace(key)) return String(status);
        return key;
    };

    const translateRiskConsequenceCategory = (category: number) => {
        const json: any = t("riskConsequenceCategory", { returnObjects: true });
        const key = json[category];
        if (isNullOrWhitespace(key)) return String(category);
        return key;
    };

    const translateRiskAreaAssessmentLevel = (level: number) => {
        const json: any = t("riskAreaAssessmentLevels", { returnObjects: true });
        const key = json[level];
        if (isNullOrWhitespace(key)) return String(level);
        return key;
    };

    const translateFrameworkStatusEnum = (status: string) => {
        const json: any = t("frameworkStatus", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateFrameworkControlStatusEnum = (status: string) => {
        const json: any = t("frameworkControlStatus", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateDocumentTypeUsage = (status: string) => {
        const json: any = t("documentTypeUsage", { returnObjects: true });
        var key = json[status];
        if (isNullOrWhitespace(key)) return status;
        return key;
    };

    const translateEvaluationEnum = (evaluation: string) => {
        const json: any = t("evaluationStatus", { returnObjects: true });
        var key = json[evaluation];
        if (isNullOrWhitespace(key)) return evaluation;
        return key;
    };

    const translatePercentage = useCallback((value: number) => {
        // Use Intl.NumberFormat with the correct locale
        return new Intl.NumberFormat(i18n.language, {
            style: "percent",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(value);
    }, []);

    const translateAnnualWheelActivityGeneralTypeEnum = (type: string) => {
        const json: any = t("annualWheelActivityGeneralTypeEnum", { returnObjects: true });
        const key = json[type];
        if (isNullOrWhitespace(key)) return type;
        return key;
    };

    const translateUserResponsibilityPropertyType = (type: number) => {
        const json: any = t("userResponsibilityPropertyType", { returnObjects: true });
        const key = json[type];
        if (isNullOrWhitespace(key)) return String(type);
        return key;
    };

    const translateUserResponsibilityEntityType = (type: number) => {
        const json: any = t("userResponsibilityEntityType", { returnObjects: true });
        const key = json[type];
        if (isNullOrWhitespace(key)) return String(type);
        return key;
    };

    return (
        <TranslationContext.Provider
            value={{
                translateString: t as TranslationStringType,
                translateNumber,
                translateDate,
                translateDateWithTime,
                translateCountry,
                translateCountriesObject: translateAllCountries,
                translateLanguage,
                changeLanguage,
                translateDocumentType,
                getBrowserLanguage: getBrowserLanguage,
                getChosenLanguage: getChosenLanguage,
                getChosenDateFormat: getChoseDateFormat,
                translateCustomListType,
                translateAnnualWheelCategoryType,
                translatePermissionTypes,
                translatepermissionTypeToolTips,
                translatePermissionLevel,
                translatePermissionListTypes,
                translatePolicyTypes,
                translatePolicyStatuses,
                translateStatus,
                translateHelpTextTypes,
                translateauditLogTypes,
                translatePropertyName,
                translateEntityType,
                translateNIS2Category,
                translateDateWithFullMonthAndYear,
                translateUtcDateToShortMonthAndYear,
                translateRiskAssessmentVersionAreas,
                translateTime,
                convertLocalHoursToUTC,
                translateShortMonth,
                getAllPropertyNames,
                getDateTimePickerLanguage: getDateTimePickerLanguage,
                translateLegalEntityAuditStatus,
                translateISO27001CategoryTypes,
                translateLegalEntityAssetRole,
                translateDateToFullMonth,
                translateDataSubjectsEnum,
                translateProcessorsEnum,
                translateOperationCriticalityLevelEnum,
                translatePlanType,
                translateCurrencyNames,
                translateContractStatusEnum,
                translateRiskScenarioCategory,
                translateRiskObservationSource,
                translateRiskObservationStatus,
                translateRiskConsequenceCategory,
                translateRiskAreaAssessmentLevel,
                translateFrameworkStatusEnum,
                translateFrameworkControlStatusEnum,
                translatePercentage,
                translateDocumentTypeUsage,
                translateAnnualWheelActivityGeneralTypeEnum,
                translateEvaluationEnum,
                translateUserResponsibilityPropertyType,
                translateUserResponsibilityEntityType,
            }}
        >
            {children}
        </TranslationContext.Provider>
    );
};
